import { type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Kafel menu mobilnego — jedno wejście w zakładkę ekranu. Klasa i treść stoją
 * osobno, bo ekran na `?tab=` montuje kafel jako `<button>` (zmiana parametru),
 * a ekran rekordu jako `<Link>` (zmiana adresu); wspólny musi być wygląd, nie
 * element.
 */
export const screenTabTileClass =
  'flex flex-col items-start gap-2.5 rounded-xl border border-neutral-200 bg-basic-white px-3.5 py-4 text-left no-underline hover:border-neutral-300';

/** Siatka kafli — dwie kolumny mieszczą się na najwęższym telefonie. */
export const screenTabTileGridClass = 'grid grid-cols-2 gap-2.5';

interface ScreenTabTileContentProps {
  icon: LucideIcon;
  label: string;
  /** Podpis pod nazwą — po co tu wchodzić. */
  hint: string;
}

const ScreenTabTileContent = ({
  icon: Icon,
  label,
  hint,
}: ScreenTabTileContentProps) => (
  <>
    <span className="flex size-8 items-center justify-center rounded-[9px] bg-accent-100 text-accent-700">
      <Icon aria-hidden size={17} />
    </span>
    <span className="text-[13.5px] font-bold leading-tight text-base-text">
      {label}
    </span>
    <span className="text-xs text-neutral-400">{hint}</span>
  </>
);

export default ScreenTabTileContent;

/**
 * Kafel kompaktowy — ikona nad nazwą, bez podpisu. Dla menu aplikacji na
 * telefonie, gdzie kilkanaście sekcji musi zmieścić się w niskim arkuszu.
 */
export const screenTabTileCompactClass =
  'relative flex h-[66px] flex-col items-center justify-center gap-1.5 rounded-[11px] border border-neutral-200 bg-neutral-50 px-1 no-underline hover:border-neutral-300';

export const screenTabTileCompactActiveClass =
  'border-accent-600 bg-accent-100 hover:border-accent-600';

/**
 * Liczba kolumn nie jest sztywna: `auto-fill` daje trzy kolumny na 320 px i
 * cztery od 390 px. Minimum 76 px, nie 70 jak w makiecie, bo „Ubezpieczenia”
 * pogrubione w 11 px nie mieści się w węższym kaflu.
 */
export const screenTabTileAutoGridClass =
  'grid grid-cols-[repeat(auto-fill,minmax(76px,1fr))] gap-2';

interface ScreenTabTileCompactContentProps {
  icon: LucideIcon;
  label: string;
  active?: boolean;
}

export const ScreenTabTileCompactContent = ({
  icon: Icon,
  label,
  active = false,
}: ScreenTabTileCompactContentProps) => (
  <>
    <Icon
      aria-hidden
      size={20}
      className={cn(active ? 'text-accent-700' : 'text-neutral-600')}
    />
    <span className="px-0.5 text-center text-[11px] font-bold leading-[1.15] text-base-text">
      {label}
    </span>
  </>
);
